/**
 * 計助ダッシュボード: 1要因ANOVA用 列フォーム
 * - 群の数（列数）と各群のデータ行数はストアと同期。1行目が群ラベル、以下が数値。
 * - 空の列ラベルは解析時に無視（従来どおり）。
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  oneWayColumnFormToGridData,
  gridDataToOneWayColumnForm,
  type OneWayColumnForm,
  normalizeKeisukeNumericInput,
} from '../../../../utils/keisukeInputFormUtils';
import type { DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import { clampKeisukeOneWayGroupCount } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import { useDebouncedFormApply } from './useDebouncedFormApply';
import styles from './KeisukeDashboardCenterPane.module.scss';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import KeisukeDashboardGridObservationCountRow from './KeisukeDashboardGridObservationCountRow';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';
import {
  clampKeisukeDashboardDataColWidthPx,
  KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX,
} from './keisukeDashboardDataGridLayout';

function oneWayColumnFormEqual(a: OneWayColumnForm, b: OneWayColumnForm): boolean {
  if (a.columnLabels.length !== b.columnLabels.length) return false;
  for (let i = 0; i < a.columnLabels.length; i++) {
    if (String(a.columnLabels[i] ?? '') !== String(b.columnLabels[i] ?? '')) return false;
  }
  if (a.valueRows.length !== b.valueRows.length) return false;
  for (let r = 0; r < a.valueRows.length; r++) {
    const rowA = a.valueRows[r];
    const rowB = b.valueRows[r];
    if (rowA.length !== rowB.length) return false;
    for (let c = 0; c < rowA.length; c++) {
      if (String(rowA[c] ?? '') !== String(rowB[c] ?? '')) return false;
    }
  }
  return true;
}

function resizeOneWayValueRows(rows: string[][], n: number, colCount: number): string[][] {
  const next = rows.slice(0, n).map((r) => {
    const x = [...r];
    while (x.length < colCount) x.push('');
    return x.slice(0, colCount);
  });
  while (next.length < n) next.push(Array(colCount).fill(''));
  return next;
}

function resizeOneWayColumnsToK(prev: OneWayColumnForm, k: number, nPerGroup: number): OneWayColumnForm {
  let columnLabels = [...prev.columnLabels];
  while (columnLabels.length < k) columnLabels.push('');
  columnLabels = columnLabels.slice(0, k);
  const valueRows = resizeOneWayValueRows(
    prev.valueRows.map((row) => {
      const x = [...row];
      while (x.length < k) x.push('');
      return x.slice(0, k);
    }),
    nPerGroup,
    k
  );
  return { columnLabels, valueRows };
}

function mergeOneWayValuePaste(
  prev: OneWayColumnForm,
  startR: number,
  startC: number,
  matrix: string[][],
  minDataRows: number
): OneWayColumnForm {
  const columnLabels = [...prev.columnLabels];
  let valueRows = prev.valueRows.map((row) => [...row]);
  const ensureCols = (minCols: number) => {
    while (columnLabels.length < minCols) {
      columnLabels.push('');
      valueRows = valueRows.map((row) => [...row, '']);
    }
  };
  for (let i = 0; i < matrix.length; i++) {
    const r = startR + i;
    while (r >= valueRows.length) {
      valueRows.push(Array(columnLabels.length).fill(''));
    }
    const line = matrix[i] ?? [];
    ensureCols(startC + line.length);
    for (let j = 0; j < line.length; j++) {
      valueRows[r]![startC + j] = normalizeKeisukeNumericInput(String(line[j] ?? ''));
    }
  }
  while (valueRows.length < minDataRows) {
    valueRows.push(Array(columnLabels.length).fill(''));
  }
  return { ...prev, columnLabels, valueRows };
}

/** startR === -1: クリップボード1行目を列見出しに、続きをデータ行0から */
function mergeOneWayPaste(
  prev: OneWayColumnForm,
  startR: number,
  startC: number,
  matrix: string[][],
  nPerGroup: number
): OneWayColumnForm {
  if (matrix.length === 0) return prev;
  const columnLabels = [...prev.columnLabels];
  let valueRows = prev.valueRows.map((row) => [...row]);
  const ensureCols = (minCols: number) => {
    while (columnLabels.length < minCols) {
      columnLabels.push('');
      valueRows = valueRows.map((row) => [...row, '']);
    }
  };
  if (startR < 0) {
    const firstLine = matrix[0] ?? [];
    ensureCols(startC + firstLine.length);
    for (let j = 0; j < firstLine.length; j++) {
      columnLabels[startC + j] = normalizeKeisukeNumericInput(String(firstLine[j] ?? ''));
    }
    const rest = matrix.slice(1);
    if (rest.length === 0) return { ...prev, columnLabels, valueRows };
    const minData = Math.max(nPerGroup, rest.length);
    return mergeOneWayValuePaste({ ...prev, columnLabels, valueRows }, 0, startC, rest, minData);
  }
  const minData = Math.max(nPerGroup, startR + matrix.length);
  return mergeOneWayValuePaste({ ...prev, columnLabels, valueRows }, startR, startC, matrix, minData);
}

type OneWayGridCell = { row: number; col: number };
type OneWayGridSelection = { a: OneWayGridCell; b: OneWayGridCell };

function normalizeOneWayGridSelection(s: OneWayGridSelection) {
  return {
    minRow: Math.min(s.a.row, s.b.row),
    maxRow: Math.max(s.a.row, s.b.row),
    minCol: Math.min(s.a.col, s.b.col),
    maxCol: Math.max(s.a.col, s.b.col),
  };
}

function oneWayGridSelectionCellCount(n: ReturnType<typeof normalizeOneWayGridSelection>) {
  return (n.maxRow - n.minRow + 1) * (n.maxCol - n.minCol + 1);
}

function oneWayCellInSelection(row: number, col: number, sel: OneWayGridSelection | null) {
  if (!sel) return false;
  const x = normalizeOneWayGridSelection(sel);
  return row >= x.minRow && row <= x.maxRow && col >= x.minCol && col <= x.maxCol;
}

function oneWayCellFromTarget(target: EventTarget | null): OneWayGridCell | null {
  const el = target as HTMLElement | null;
  if (!el?.closest) return null;
  if (el.closest('[data-ow-ignore-selection="1"]')) return null;
  const inp = el.closest('input[data-ow-row]') as HTMLInputElement | null;
  if (!inp) return null;
  const rs = inp.getAttribute('data-ow-row');
  const cs = inp.getAttribute('data-ow-col');
  if (rs == null || cs == null) return null;
  return { row: parseInt(rs, 10), col: parseInt(cs, 10) };
}

function getOneWayPasteAnchor(sel: OneWayGridSelection | null, focus: OneWayGridCell): OneWayGridCell {
  if (!sel) return focus;
  const n = normalizeOneWayGridSelection(sel);
  if (oneWayGridSelectionCellCount(n) <= 1) return focus;
  return { row: n.minRow, col: n.minCol };
}

function buildTsvFromOneWaySelection(
  f: OneWayColumnForm,
  norm: ReturnType<typeof normalizeOneWayGridSelection>
): string {
  const lines: string[][] = [];
  for (let r = norm.minRow; r <= norm.maxRow; r++) {
    const line: string[] = [];
    for (let c = norm.minCol; c <= norm.maxCol; c++) {
      if (r < 0) {
        line.push(String(f.columnLabels[c] ?? ''));
      } else {
        line.push(String(f.valueRows[r]?.[c] ?? ''));
      }
    }
    lines.push(line);
  }
  return lines.map((ln) => ln.join('\t')).join('\n');
}

export interface KeisukeDashboardDataFormOneWayColumnsProps {
  gridData: DataRow[];
  gridHeaders: string[];
  groupColumn?: string;
  valueColumn?: string;
  onApply: (data: DataRow[], headers: string[]) => void;
  disabled?: boolean;
  nPerGroup: number;
  onNPerGroupChange: (n: number) => void;
  /** 群の数（データ列数）。ストアの `oneWayGroupCount` */
  groupCount: number;
  onGroupCountChange: (n: number) => void;
  /** 他入力形式と共有するデータ列幅（px）。中央ペイン `longFormatTwoColWidthPx` */
  linkedDataColumnWidthPx?: number;
  onLinkedDataColumnWidthCommit?: (px: number) => void;
}

export default function KeisukeDashboardDataFormOneWayColumns({
  gridData,
  gridHeaders,
  groupColumn,
  valueColumn,
  onApply,
  disabled = false,
  nPerGroup,
  onNPerGroupChange,
  groupCount,
  onGroupCountChange,
  linkedDataColumnWidthPx,
  onLinkedDataColumnWidthCommit,
}: KeisukeDashboardDataFormOneWayColumnsProps) {
  const { t } = useTranslation();
  const fieldIdPrefix = useId();
  const skipSyncFromGridRef = useRef(false);
  const suppressGridSyncUntilRef = useRef(0);
  const oneWayColDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const gridAnchorRef = useRef<OneWayGridCell | null>(null);
  const [gridSelection, setGridSelection] = useState<OneWayGridSelection | null>(null);
  const [isDragSelecting, setIsDragSelecting] = useState(false);
  const [oneWayDataColWidthPx, setOneWayDataColWidthPx] = useState(() =>
    clampKeisukeDashboardDataColWidthPx(
      linkedDataColumnWidthPx ?? KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX
    )
  );
  const [form, setForm] = useState<OneWayColumnForm>(() => {
    const fromGrid = gridDataToOneWayColumnForm(gridData, gridHeaders, groupColumn, valueColumn);
    if (fromGrid) return fromGrid;
    const k = clampKeisukeOneWayGroupCount(groupCount);
    const labels = Array(k).fill('');
    const valueRows = Array.from({ length: Math.max(1, nPerGroup) }, () => Array(k).fill(''));
    return { columnLabels: labels, valueRows };
  });

  useEffect(() => {
    if (linkedDataColumnWidthPx == null) return;
    const next = clampKeisukeDashboardDataColWidthPx(linkedDataColumnWidthPx);
    setOneWayDataColWidthPx((prev) => (prev === next ? prev : next));
  }, [linkedDataColumnWidthPx]);

  const prevGroupCountRef = useRef(groupCount);
  useEffect(() => {
    if (prevGroupCountRef.current === groupCount) return;
    prevGroupCountRef.current = groupCount;
    setForm((prev) => resizeOneWayColumnsToK(prev, clampKeisukeOneWayGroupCount(groupCount), nPerGroup));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nPerGroup のみ変化時は行リサイズ用の別 effect に任せる
  }, [groupCount]);

  const prevNPerGroupRef = useRef(nPerGroup);
  useEffect(() => {
    if (prevNPerGroupRef.current === nPerGroup) return;
    prevNPerGroupRef.current = nPerGroup;
    setForm((prev) => ({
      ...prev,
      valueRows: resizeOneWayValueRows(prev.valueRows, nPerGroup, prev.columnLabels.length),
    }));
  }, [nPerGroup]);

  useEffect(() => {
    if (Date.now() < suppressGridSyncUntilRef.current) {
      return;
    }
    if (skipSyncFromGridRef.current) {
      skipSyncFromGridRef.current = false;
      return;
    }
    const fromGrid = gridDataToOneWayColumnForm(gridData, gridHeaders, groupColumn, valueColumn);
    if (fromGrid) {
      setForm((prev) => (oneWayColumnFormEqual(fromGrid, prev) ? prev : fromGrid));
      const len = fromGrid.valueRows.length;
      if (len !== nPerGroup) {
        onNPerGroupChange(len);
      }
      const colCount = fromGrid.columnLabels.length;
      const k = clampKeisukeOneWayGroupCount(Math.max(2, colCount));
      if (k !== groupCount) {
        onGroupCountChange(k);
      }
      return;
    }
    if (gridHeaders.length === 0) {
      const k = clampKeisukeOneWayGroupCount(groupCount);
      setForm({
        columnLabels: Array(k).fill(''),
        valueRows: Array.from({ length: Math.max(1, nPerGroup) }, () => Array(k).fill('')),
      });
    }
    // nPerGroup / groupCount は依存に含めない（スピナー変更時に未更新グリッドで戻さない）
  }, [gridData, gridHeaders, groupColumn, valueColumn, onNPerGroupChange, onGroupCountChange, groupCount, nPerGroup]);

  useEffect(() => {
    return () => {
      oneWayColDragRef.current = null;
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
  }, []);

  const applyForm = useCallback(() => {
    const { headers, data } = oneWayColumnFormToGridData(form);
    suppressGridSyncUntilRef.current = Date.now() + 950;
    skipSyncFromGridRef.current = true;
    onApply(data, headers);
  }, [form, onApply]);

  useDebouncedFormApply(applyForm, [form, applyForm], { delayMs: 280, skipInitial: true });

  const pushApplyFromForm = useCallback(
    (f: OneWayColumnForm) => {
      suppressGridSyncUntilRef.current = Date.now() + 950;
      skipSyncFromGridRef.current = true;
      const { headers, data } = oneWayColumnFormToGridData(f);
      onApply(data, headers);
    },
    [onApply]
  );

  useEffect(() => {
    if (!isDragSelecting) return;
    const onMove = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = oneWayCellFromTarget(el);
      if (cell && gridAnchorRef.current) {
        setGridSelection({ a: gridAnchorRef.current, b: cell });
      }
    };
    const onUp = () => setIsDragSelecting(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp, true);
    document.addEventListener('pointercancel', onUp, true);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [isDragSelecting]);

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current?.contains(t)) return;
      setGridSelection(null);
      gridAnchorRef.current = null;
    };
    document.addEventListener('mousedown', onDocDown, true);
    return () => document.removeEventListener('mousedown', onDocDown, true);
  }, []);

  const onTablePointerDownCapture = (e: React.PointerEvent<HTMLTableElement>) => {
    if (disabled || e.button !== 0) return;
    const cell = oneWayCellFromTarget(e.target);
    if (!cell) return;
    if (e.shiftKey && gridAnchorRef.current) {
      setGridSelection({ a: gridAnchorRef.current, b: cell });
      return;
    }
    gridAnchorRef.current = cell;
    setGridSelection({ a: cell, b: cell });
    setIsDragSelecting(true);
  };

  const onTableCopyCapture = (e: React.ClipboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    const t = e.target as HTMLElement;
    if (t.tagName !== 'INPUT') return;
    const focusedCell = oneWayCellFromTarget(t);
    const effectiveSel =
      gridSelection ??
      (focusedCell ? ({ a: focusedCell, b: focusedCell } satisfies OneWayGridSelection) : null);
    if (!effectiveSel) return;
    const norm = normalizeOneWayGridSelection(effectiveSel);
    const area = oneWayGridSelectionCellCount(norm);
    if (area <= 1) {
      const inp = t as HTMLInputElement;
      if (
        inp.selectionStart != null &&
        inp.selectionEnd != null &&
        inp.selectionStart !== inp.selectionEnd
      ) {
        return;
      }
    }
    e.preventDefault();
    e.stopPropagation();
    const tsv = buildTsvFromOneWaySelection(form, norm);
    e.clipboardData?.setData('text/plain', tsv);
  };

  const onTableKeyDownCapture = (e: React.KeyboardEvent<HTMLTableElement>) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setGridSelection(null);
      gridAnchorRef.current = null;
    }
  };

  const onPasteHeader = useCallback(
    (e: React.ClipboardEvent, startC: number) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      const anchor = getOneWayPasteAnchor(gridSelection, { row: -1, col: startC });
      setForm((prev) => {
        const next = mergeOneWayPaste(prev, anchor.row, anchor.col, matrix, nPerGroup);
        if (next.valueRows.length > nPerGroup) {
          queueMicrotask(() => onNPerGroupChange(next.valueRows.length));
        }
        const newK = clampKeisukeOneWayGroupCount(next.columnLabels.length);
        if (newK > groupCount) {
          queueMicrotask(() => onGroupCountChange(newK));
        }
        queueMicrotask(() => pushApplyFromForm(next));
        return next;
      });
    },
    [disabled, gridSelection, nPerGroup, groupCount, onNPerGroupChange, onGroupCountChange, pushApplyFromForm]
  );

  const onPasteCell = useCallback(
    (e: React.ClipboardEvent, rowIndex: number, colIndex: number) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      const anchor = getOneWayPasteAnchor(gridSelection, { row: rowIndex, col: colIndex });
      setForm((prev) => {
        const next = mergeOneWayPaste(prev, anchor.row, anchor.col, matrix, nPerGroup);
        if (next.valueRows.length > nPerGroup) {
          queueMicrotask(() => onNPerGroupChange(next.valueRows.length));
        }
        const newK = clampKeisukeOneWayGroupCount(next.columnLabels.length);
        if (newK > groupCount) {
          queueMicrotask(() => onGroupCountChange(newK));
        }
        queueMicrotask(() => pushApplyFromForm(next));
        return next;
      });
    },
    [disabled, gridSelection, nPerGroup, groupCount, onNPerGroupChange, onGroupCountChange, pushApplyFromForm]
  );

  const gridCellClass = (row: number, col: number) =>
    oneWayCellInSelection(row, col, gridSelection) ? grid.tTestTwoColCellSelected : undefined;

  const setLabel = (colIndex: number, value: string) => {
    setForm((prev) => {
      const next = [...prev.columnLabels];
      next[colIndex] = value;
      return { ...prev, columnLabels: next };
    });
  };

  const setCell = (rowIndex: number, colIndex: number, value: string) => {
    const v = normalizeKeisukeNumericInput(value);
    setForm((prev) => {
      const rows = prev.valueRows.map((row, r) =>
        r === rowIndex ? row.map((cell, c) => (c === colIndex ? v : cell)) : [...row]
      );
      return { ...prev, valueRows: rows };
    });
  };

  const cols = form.columnLabels.length;

  const oneWayResizeHostClass = (c: number) =>
    cols === 1 || c < cols - 1 ? grid.keisukeTTestColResizeHost : undefined;

  const clearOneWayColResizeChrome = () => {
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
  };

  const onOneWayColResizePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    oneWayColDragRef.current = { startX: e.clientX, startW: oneWayDataColWidthPx };
  };

  const onOneWayColResizePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = oneWayColDragRef.current;
    if (!sess) return;
    const dx = e.clientX - sess.startX;
    setOneWayDataColWidthPx(clampKeisukeDashboardDataColWidthPx(sess.startW + dx));
  };

  const onOneWayColResizePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const sess = oneWayColDragRef.current;
    if (sess) {
      const finalW = clampKeisukeDashboardDataColWidthPx(e.clientX - sess.startX + sess.startW);
      setOneWayDataColWidthPx(finalW);
      onLinkedDataColumnWidthCommit?.(finalW);
    }
    oneWayColDragRef.current = null;
    clearOneWayColResizeChrome();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  const onOneWayColResizeLostCapture = () => {
    oneWayColDragRef.current = null;
    clearOneWayColResizeChrome();
  };

  return (
    <div className={`${styles.formWrap} ${styles.formWrapDense}`}>
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.nGroupsOneWay')}
        value={groupCount}
        onChange={onGroupCountChange}
        onBlur={applyForm}
        disabled={disabled}
        min={2}
        max={30}
      />
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.nPerGroupOneWay')}
        value={nPerGroup}
        onChange={onNPerGroupChange}
        onBlur={applyForm}
        disabled={disabled}
      />
      <div className={grid.keisukeAnovaTableWrap}>
        <div className={grid.tableScroll}>
          <table
            ref={tableRef}
            className={grid.keisukeAnovaGridTable}
            onPointerDownCapture={onTablePointerDownCapture}
            onCopyCapture={onTableCopyCapture}
            onKeyDownCapture={onTableKeyDownCapture}
          >
            <colgroup>
              <col className={grid.keisukeRowNumCol} />
              {form.columnLabels.map((_, c) => (
                <col
                  key={c}
                  className={grid.keisukeAnovaColData}
                  style={{ width: `${oneWayDataColWidthPx}px` }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className={grid.keisukeRowNumCell} aria-hidden>#</th>
                {form.columnLabels.map((label, c) => (
                  <th
                    key={c}
                    className={[oneWayResizeHostClass(c), gridCellClass(-1, c)].filter(Boolean).join(' ') || undefined}
                  >
                    <div className={grid.spreadsheetThFlex}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputHeader}
                        id={`${fieldIdPrefix}-ow-h-${c}`}
                        name={`${fieldIdPrefix}-ow-h-${c}`}
                        data-ow-row={-1}
                        data-ow-col={c}
                        value={label}
                        onChange={(e) => setLabel(c, e.target.value)}
                        onBlur={applyForm}
                        onPaste={(e) => onPasteHeader(e, c)}
                        placeholder={t('keisuke.dashboard.inputForm.groupLabel', { num: c + 1 })}
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.groupLabel', { num: c + 1 })}
                      />
                    </div>
                    {(cols === 1 || c < cols - 1) && (
                      <button
                        type="button"
                        className={grid.keisukeTTestColResizeHandle}
                        data-ow-ignore-selection="1"
                        tabIndex={-1}
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.resizeOneWayDataColumnsUniform')}
                        onPointerDown={onOneWayColResizePointerDown}
                        onPointerMove={onOneWayColResizePointerMove}
                        onPointerUp={onOneWayColResizePointerUp}
                        onPointerCancel={onOneWayColResizePointerUp}
                        onLostPointerCapture={onOneWayColResizeLostCapture}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.valueRows.map((row, r) => (
                <tr key={r}>
                  <td className={grid.keisukeRowNumCell} aria-hidden>{r + 1}</td>
                  {row.map((cell, c) => (
                    <td key={c} className={gridCellClass(r, c)}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputCell}
                        id={`${fieldIdPrefix}-ow-r${r}-c${c}`}
                        name={`${fieldIdPrefix}-ow-r${r}-c${c}`}
                        data-ow-row={r}
                        data-ow-col={c}
                        value={cell}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        onBlur={applyForm}
                        onPaste={(e) => onPasteCell(e, r, c)}
                        inputMode="decimal"
                        disabled={disabled}
                        aria-label={t('keisuke.dashboard.inputForm.cell', {
                          row: r + 1,
                          col: c + 1,
                        })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <KeisukeClipboardHintBar />
    </div>
  );
}
